import { ScoreColorVariant, ScoreDescriptions, ScoreRange } from '@vizit/constants';

/**
 * Internal helper function to determine the score range category from a numeric score
 *
 * @param score - The score value to categorize
 * @returns ScoreRange enum value representing the score category
 */
function determineScoreCategory(score?: number | null): ScoreRange {
  if (score === undefined || score === null) return ScoreRange.NotApplicable;

  const value = Number(score);

  if (value < 20) return ScoreRange.VeryLow;
  if (value < 40) return ScoreRange.Low;
  if (value < 60) return ScoreRange.Moderate;
  if (value < 80) return ScoreRange.High;

  return ScoreRange.VeryHigh;
}

/**
 * Map ScoreRange to ScoreDescriptions
 *
 * @param scoreRange - The ScoreRange to convert
 * @returns Corresponding ScoreDescriptions enum value
 */
function mapScoreRangeToDescription(scoreRange: ScoreRange): ScoreDescriptions {
  switch (scoreRange) {
    case ScoreRange.VeryLow:
      return ScoreDescriptions.VeryLow;
    case ScoreRange.Low:
      return ScoreDescriptions.Low;
    case ScoreRange.Moderate:
      return ScoreDescriptions.Moderate;
    case ScoreRange.High:
      return ScoreDescriptions.High;
    case ScoreRange.VeryHigh:
      return ScoreDescriptions.VeryHigh;
    default:
      return ScoreDescriptions.NotApplicable;
  }
}

/**
 * Get the score description based on the score value
 *
 * @param score - The score value to get description for
 * @returns ScoreDescriptions enum value
 */
function getScoreDescription(score?: number | null): ScoreDescriptions {
  const scoreRange = determineScoreCategory(score);
  return mapScoreRangeToDescription(scoreRange);
}

/**
 * Get the score range based on the score value
 *
 * @param score - The score value to get range for
 * @returns ScoreRange enum value
 */
function getScoreRange(score?: number | null): ScoreRange {
  return determineScoreCategory(score);
}

/**
 * Determine if a vizit score is valid. A valid score is a number that is not NaN, undefined, or null. (0 is a valid score).
 *
 * @param {number | null | undefined} score - The score to check if it has a value.
 * @returns {boolean} - Whether the score has a value.
 */
function hasScore(score?: number | null): boolean {
  return !Number.isNaN(Number(score)) && score !== undefined && score !== null;
}

/**
 * Serialize a score to a string format
 *
 * @param score - The score to serialize
 * @returns Formatted score string or 'N/A'
 */
function serializeScore(score?: number | null) {
  if (!hasScore(score)) return 'N/A';

  return Math.round(Number(score)).toString();
}

/**
 * Get the shared color variant for a score value.
 * Used by ScorePill, ScoreSticker, and other score-colored components to ensure consistent colors.
 *
 * @param score - The score value (0-100) or null
 * @returns ScoreColorVariant enum value
 */
function getScoreColorVariant(score: number | null): ScoreColorVariant {
  if (score === null || score === 0) {
    return ScoreColorVariant.NotApplicable;
  }

  const range = determineScoreCategory(score);

  switch (range) {
    case ScoreRange.VeryLow:
      return ScoreColorVariant.VeryLow;
    case ScoreRange.Low:
      return ScoreColorVariant.Low;
    case ScoreRange.Moderate:
      return ScoreColorVariant.Moderate;
    case ScoreRange.High:
      return ScoreColorVariant.High;
    case ScoreRange.VeryHigh:
      return ScoreColorVariant.VeryHigh;
    default:
      return ScoreColorVariant.NotApplicable;
  }
}

/**
 * Resolves the display score for an image.
 *
 * If the image is a hero (image_type === 'hero') and has a hero_score, returns hero_score rounded.
 * If the image is a hero but has no hero_score, returns null (displayed as "N/A").
 * For non-hero images, falls back to vizit_score.
 */
function getHeroImageScore({
  imageType,
  heroScore,
  vizitScore,
}: {
  imageType: string | undefined | null;
  heroScore: number | undefined | null;
  vizitScore: string | undefined | null;
}): number | null {
  if (imageType === 'hero') {
    return heroScore != null ? Math.round(heroScore) : null;
  }

  if (vizitScore != null && vizitScore !== '') {
    return Number(vizitScore);
  }

  return null;
}

/**
 * Determines if a hero asset should be marked as unscored.
 *
 * @param params.omitScore - Whether the asset has omit_score flag set.
 * @param params.score - The computed hero score (from getHeroImageScore).
 * @param params.isPending - Whether the asset data is still pending.
 * @param params.isLoading - Whether the asset data is still loading.
 * @returns true if the asset should display as unscored.
 */
function isHeroUnscored(params: {
  omitScore?: boolean | null;
  score: number | null;
  isPending?: boolean;
  isLoading?: boolean;
}): boolean {
  const { omitScore, score, isPending, isLoading } = params;
  return !!omitScore || (score === null && !isPending && !isLoading);
}

export {
  getHeroImageScore,
  getScoreDescription,
  getScoreRange,
  getScoreColorVariant,
  hasScore,
  isHeroUnscored,
  serializeScore,
};
